import { useTranslation } from "react-i18next";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../app/hooks";
import ClickIconMenu from "../../../../components/essentials/icon-buttons/click-icon/ClickIconMenu";
import { getDescriptionHtml } from "../../../../components/essentials/input/text-area/RichTextInput";
import UserCard, {
	UserPreviewInfo,
} from "../../../../components/essentials/user-card/UserCard";
import { PostInfo } from "../../../../components/post/Post";
import { selectUser } from "../../../../core/auth/authSlice";
import { addSuccessMessage } from "../../../../core/errors/errorSlice";
import { PostApiRoutes } from "../../../../shared/api-routes/post/post.api-routes";
import { User } from "../../../../shared/models";
import http from "../../../../shared/utils/http";
import { resolveMediaUrl } from "../../../../shared/utils/mediaUtils";
import { capitalizeName } from "../../../../shared/utils/util";
import PostPage from "../../../post-page/PostPage";
import "./Message.scss";

export interface IMessage {
	id: string;
	sender: string;
	text: string;
	createdAt: string;
	chatId: string;
	editedAt?: string;
	type?: "post" | "system";
	postId?: string;
	read: boolean;
	receiver?: string;
}

export interface MessageProps {
	message: IMessage;
	other: UserPreviewInfo | undefined;
	startEditing: any;
	onDelete: any;
	hasName?: boolean;
	scrollAction?: () => void;
}

export interface SenderInfo {
	sender: string;
	time: string;
}

const Message: React.FC<MessageProps> = ({
	message,
	other,
	startEditing,
	onDelete,
	hasName,
	scrollAction,
}) => {
	const { actorId } = useSelector(selectUser) as User;

	const { t } = useTranslation();

	const [senderInfo, setSenderInfo] = useState<SenderInfo>();
	const [sentByMe, setSentByMe] = useState<boolean>();

	const formatTime = (dateString: string) => {
		const date = new Date(dateString);

		const hour = date.getUTCHours();
		const minute = date.getUTCMinutes();
		return `${hour}:${minute}`;
	};

	useEffect(() => {
		if (!other) return;
		setSenderInfo({
			sender: actorId === message.sender ? "You" : `${capitalizeName(other.name)} ${capitalizeName(other.surname)}`,
			time: formatTime(message.createdAt),
		});

		setSentByMe(actorId === message.sender);
	}, [other]);

	const [isHovering, setHovering] = useState<boolean>(false);
	const [isMenuOpen, setMenuOpen] = useState(false);

	const onCloseMenu = () => {
		setHovering(false);
	};

	const menuOptions = () => {
		const copyOption = {
			text: "Copy",
			action: () => {
				navigator.clipboard.writeText(message.text);
				appDispatch(
					addSuccessMessage({ title: "Message copied to clipboard" })
				);
			},
		};

		const forwardOption = {
			text: "Forward",
		};

		const editOption = {
			text: "Edit Message",
			action: () => {
				startEditing(message.id, message.text);
				setHovering(false);
			},
		};

		const deleteOption = {
			text: "Delete Message",
			action: () => onDelete(message.id),
			color: "red",
		};

		let options = [];

		const isMine = actorId === message.sender;
		const isText = !message.type;

		if (isText) options.push(copyOption);
		if (isMine && isText) options.push(editOption);
		if (isMine) options.push(deleteOption);
		options.push(forwardOption);
		return options;
	};

	const appDispatch = useAppDispatch();

	const [postInfo, setPostInfo] = useState<PostInfo | null>(null);

	useEffect(() => {
		if (message.type === "post" && message.postId) {
			http
				.get(PostApiRoutes.GET_POST(message.postId))
				.then((post: PostInfo) => {
					setPostInfo(post);
					if (post.type === "image" && (post.thumbUrl || post.imageUrl)) {
						setPostImg(resolveMediaUrl(post.thumbUrl || post.imageUrl) || null);
					} else {
						setPostImg(null);
					}
					scrollAction && setTimeout(scrollAction, 150);
				})
				.catch(() => setPostInfo(null));
		}
	}, [message.type, message.postId]);

	const [postImg, setPostImg] = useState<string | null>(null);

	const navigate = useNavigate();

	const [postPageVisible, setPostPageVisible] = useState<string | null>(null);

	useEffect(() => {
		if (!postPageVisible) {
			scrollAction && setTimeout(scrollAction, 150);
		}
	}, [postPageVisible]);

	// Render system messages differently
	if (message.type === "system") {
		const senderName = other ? `${capitalizeName(other.name)} ${capitalizeName(other.surname)}` : "";
		return (
			<div className="message__container message__system">
				<div className="message__baloon-system">
					<span className="message__system-text">{t(message.text, { name: senderName })}</span>
				</div>
				{hasName && (
					<p className="message__system-time">{senderInfo?.time}</p>
				)}
			</div>
		);
	}

	return (
		<div
			className={`message__container ${
				sentByMe ? "message__me" : "message__you"
			}`}
			onMouseEnter={() => setHovering(true)}
			onMouseLeave={() => {
				!isMenuOpen && setHovering(false);
			}}
		>
			{hasName && (
				<div
					className={`message__sender-wrapper ${
						sentByMe
							? "message__sender-wrapper-me"
							: "message__sender-wrapper-you"
					}`}
				>
					<p className="message__sender">{senderInfo?.sender}</p>
					<p className="message__time">{senderInfo?.time}</p>
				</div>
			)}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					flexDirection: sentByMe ? "row" : "row-reverse",
					justifyContent: "flex-end",
				}}
			>
				{isHovering && (
					<ClickIconMenu
						isMenuOpen={isMenuOpen}
						setMenuOpen={setMenuOpen}
						icon="dots"
						size="s"
						options={menuOptions()}
						onClose={onCloseMenu}
					/>
				)}
				{message.type === "post" ? (
					<div className="message__baloon-post">
						{postInfo ? (
							<>
								<div
									className="message__post"
									onClick={() => setPostPageVisible(postInfo.id)}
								>
									<div style={{ pointerEvents: "none" }}>
										<UserCard
											userInfo={postInfo.author}
											size="s"
										/>
									</div>

									<div
										style={{
											display: "flex",
											flexDirection: "column",
											justifyContent: "center",
											alignItems: "center",
										}}
									>
										<div
											className="description"
											style={{ width: "100%" }}
											dangerouslySetInnerHTML={{
												__html: getDescriptionHtml(postInfo.description || ""),
											}}
										/>
										{postImg && (
											<div>
												<img
													style={{ width: "200px" }}
													src={postImg}
												/>
											</div>
										)}
									</div>
								</div>
								{postPageVisible &&
									createPortal(
										<PostPage
											postId={postPageVisible}
											closePostPage={() => setPostPageVisible(null)}
										/>,
										document.getElementById("board-outlet")!
									)}
							</>
						) : (
							<div className="message__baloon-text">
								[Post is loading, or there is an error]
							</div>
						)}
					</div>
				) : (
					<div className="message__baloon-text">{message.text}</div>
				)}
			</div>
			{message.editedAt && (
				<p
					className="edited-label"
					style={{
						alignSelf: actorId === message.sender ? "flex-end" : "flex-start",
					}}
				>
					Edited
				</p>
			)}
		</div>
	);
};

export default Message;
